import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Stack,
  Button,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  alpha,
  useTheme,
  Paper,
  Grid,
  IconButton,
  Card,
} from "@mui/material";
import {
  AccessTime,
  Room,
  Info,
  Cancel,
  Warning,
  ArrowBackIosNew,
  ArrowForwardIos,
  MonetizationOn,
  History,
  EventAvailable,
  Timer,
} from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { getOneBooking, cancelBooking } from "../redux/slices/booking/thunk";
import useBaseImageUrl from "../hooks/useBaseImageUrl";
import LoadingPage from "../components/LoadingComponent";

export default function BookingDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const baseUrl = useBaseImageUrl();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const mode = theme.palette.mode;

  const { oneBooking, loading } = useSelector((state) => state.booking);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);

  useEffect(() => {
    dispatch(getOneBooking(id));
  }, [dispatch, id]);

  if (loading || !oneBooking) return <LoadingPage />;

  const getStatusColor = (status) => {
    const map = {
      pending: {
        color: theme.palette.primary.main,
        label: t("bookings.status.pending"),
        // icon: <Timer fontSize="small" />,
      },
      active: {
        color: "#4caf50",
        label: t("bookings.status.active"),
        // icon: <EventAvailable fontSize="small" />,
      },
      completed: {
        color: "#757575",
        label: t("bookings.status.completed"),
        // icon: <History fontSize="small" />,
      },
      cancelled: {
        color: theme.palette.error.main,
        label: t("bookings.status.cancelled"),
        // icon: <Cancel fontSize="small" />,
      },
    };
    return map[status] || { color: "#757575", label: status };
  };

  const statusInfo = getStatusColor(oneBooking.status);
  const start = new Date(oneBooking.reservedStart);
  const end = new Date(oneBooking.reservedEnd);

  // حساب السعر لو كان 0 في الداتا (بناءً على الثمن لكل ساعة)
  const calculateEstimate = () => {
    if (oneBooking.finalPrice > 0) return oneBooking.finalPrice;
    const duration = Math.abs(end - start) / 36e5;
    const rate =
      oneBooking.currentType === "single"
        ? oneBooking.room?.price_single_hour
        : oneBooking.room?.price_multi_hour;
    return Math.round(duration * (rate || 0));
  };

  // حساب الوقت المتبقي
  const getRemainingTime = () => {
    const diff = start - new Date();
    if (diff <= 0) return t("details.starting_now");
    const hours = Math.floor(diff / 36e5);
    const mins = Math.floor((diff % 36e5) / 60000);
    return isRtl ? `متبقي ${hours} س و ${mins} د` : `${hours}h ${mins}m left`;
  };

  const handleConfirmCancel = async () => {
    await dispatch(cancelBooking(id));
    setOpenCancelDialog(false);
    navigate("/MyBookings");
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", pb: 8 }}>
      {/* Header مع استخدام الـ Header Color من الثيم */}
      <Box
        sx={{
          py: 2,
        //   bgcolor: theme.palette.primary.header,
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        }}
      >
        <Container maxWidth="md">
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <IconButton
              onClick={() => navigate(-1)}
              sx={{ bgcolor: theme.palette.background.paper, boxShadow: 1 }}
            >
              {isRtl ? (
                <ArrowForwardIos fontSize="small" />
              ) : (
                <ArrowBackIosNew fontSize="small" />
              )}
            </IconButton>
            <Typography variant="h6" fontWeight={900} color="text.primary">
              {t("details.booking_details_title")}
            </Typography>
            <Box width={40} />
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ mt: 3 }}>
        <Card sx={{ border: "none", overflow: "hidden" }}>
          {/* صورة الغرفة مع Gradient Overlay */}
          <Box sx={{ position: "relative", height: { xs: 200, sm: 280 } }}>
            <Box
              component="img"
              src={`${baseUrl}${oneBooking.room?.image}`}
              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `linear-gradient(to bottom, transparent, ${theme.palette.background.paper})`,
                display: "flex",
                alignItems: "flex-end",
                p: 3,
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-end"
                width="100%"
              >
                <Box>
                  <Chip
                    icon={statusInfo.icon}
                    label={statusInfo.label}
                    sx={{
                      bgcolor: statusInfo.color,
                      color: "#fff",
                      fontWeight: 900,
                      mb: 1,
                    }}
                  />
                  <Typography
                    variant="h4"
                    fontWeight={1000}
                    color="text.primary"
                  >
                    {isRtl
                      ? oneBooking.room?.title_ar
                      : oneBooking.room?.title_en}
                  </Typography>
                </Box>
                {oneBooking.status === "pending" && (
                  <Paper
                    sx={{
                      p: 1,
                      px: 2,
                      borderRadius: 3,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      border: `1px solid ${theme.palette.primary.main}`,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="primary"
                      fontWeight={900}
                      display="block"
                    >
                      {t("details.starts_in")}
                    </Typography>
                    <Typography variant="body2" fontWeight={1000}>
                      {getRemainingTime()}
                    </Typography>
                  </Paper>
                )}
              </Stack>
            </Box>
          </Box>

          <Box sx={{ p: { xs: 2, sm: 4 } }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Stack spacing={3}>
                  <DetailItem
                    icon={<AccessTime color="primary" />}
                    label={t("details.time")}
                    value={`${start.toLocaleTimeString(isRtl ? "ar-EG" : "en-US", { hour: "2-digit", minute: "2-digit" })} - ${end.toLocaleTimeString(isRtl ? "ar-EG" : "en-US", { hour: "2-digit", minute: "2-digit" })}`}
                  />
                  <DetailItem
                    icon={<History color="primary" />}
                    label={t("details.duration")}
                    value={`${Math.round(Math.abs(end - start) / 36e5)} ${t("bookings.hours_short")}`}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Stack spacing={3}>
                  <DetailItem
                    icon={<Room color="primary" />}
                    label={t("details.booking_type")}
                    value={
                      oneBooking.currentType === "single"
                        ? t("bookings.single")
                        : t("bookings.multi")
                    }
                  />
                  {/* Price Box */}
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 4,
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <MonetizationOn sx={{ fontSize: 18 }} />
                      <Typography variant="caption" fontWeight={700}>
                        {t("details.expected_price")}
                      </Typography>
                    </Stack>
                    <Typography variant="h4" fontWeight={1000}>
                      {calculateEstimate()}{" "}
                      <Typography
                        component="span"
                        fontWeight={900}
                        variant="body2"
                      >
                        EGP
                      </Typography>
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
            </Grid>

            <Divider
              sx={{
                my: 4,
                borderStyle: "dashed",
                borderColor: alpha(theme.palette.primary.main, 0.2),
              }}
            />

            {/* Management Rules Card */}
            <Typography
              variant="subtitle1"
              fontWeight={1000}
              gutterBottom
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                color: theme.palette.primary.main,
              }}
            >
              <Info fontSize="small" /> {t("details.management_rules")}
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 4,
                borderStyle: "dashed",
                bgcolor: theme.palette.primary.softBackground,
                borderColor: theme.palette.primary.borderColor,
              }}
            >
              <Stack spacing={1.5}>
                <RuleRow text={t("details.rule_1")} />
                <RuleRow text={t("details.rule_2")} />
                <RuleRow text={t("details.rule_3")} />
              </Stack>
            </Paper>
          </Box>
        </Card>

        {/* Action Button */}
        {oneBooking.status === "pending" && (
          <Box sx={{ mt: 4 }}>
            <Button
              fullWidth
              variant="outlined"
              color="primary"
              size="large"
              onClick={() => setOpenCancelDialog(true)}
              sx={{
                borderRadius: 4,
                py: 2,
                fontWeight: 1000,
                borderWidth: 2,
                "&:hover": { borderWidth: 2 },
              }}
            >
              {t("details.cancel_booking_btn")}
            </Button>
          </Box>
        )}
      </Container>

      {/* Cancel Dialog */}
      <Dialog
        open={openCancelDialog}
        onClose={() => setOpenCancelDialog(false)}
        PaperProps={{ sx: { borderRadius: 6, p: 1 } }}
      >
        <DialogTitle sx={{ textAlign: "center", pb: 0 }}>
          <Box
            sx={{
              bgcolor: alpha(theme.palette.error.main, 0.1),
              width: 60,
              height: 60,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <Warning color="error" sx={{ fontSize: 35 }} />
          </Box>
          <Typography variant="h6" fontWeight={1000}>
            {t("details.confirm_cancel_title")}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            textAlign="center"
            color="text.secondary"
            sx={{ fontWeight: 600 }}
          >
            {t("details.confirm_cancel_body")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ flexDirection: "column", gap: 1, px: 3, pb: 3 }}>
          <Button
            fullWidth
            onClick={handleConfirmCancel}
            variant="contained"
            sx={{ bgcolor: theme.palette.error.main, py: 1.5 }}
          >
            {t("details.confirm_cancel_btn")}
          </Button>
          <Button
            fullWidth
            onClick={() => setOpenCancelDialog(false)}
            sx={{ color: "text.secondary" }}
          >
            {t("common.back")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// مكون فرعي لعرض تفصيلة واحدة
function DetailItem({ icon, label, value }) {
  const theme = useTheme();
  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Box
        sx={{
          bgcolor: theme.palette.primary.softBackground,
          p: 1.5,
          borderRadius: "16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: `1px solid ${alpha(theme.palette.primary.main, 0.05)}`,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={800}
          display="block"
          sx={{ textTransform: "uppercase" }}
        >
          {label}
        </Typography>
        <Typography variant="body1" fontWeight={1000}>
          {value}
        </Typography>
      </Box>
    </Stack>
  );
}

// مكون فرعي لعرض سطر من القواعد
function RuleRow({ text }) {
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          bgcolor: "primary.main",
          mt: 1,
          flexShrink: 0,
        }}
      />
      <Typography variant="caption" fontWeight={700} color="text.secondary">
        {text}
      </Typography>
    </Stack>
  );
}
